import React, { useEffect, useState } from "react";
import swal from "sweetalert";
import { Edit, Trash2, PlusCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import axiosConfig from "../../AxiosConfig";

export default function Payment() {
  const navigate = useNavigate();

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch Payment Data
  const fetchPayments = async () => {
    try {
      setLoading(true);

      const user = JSON.parse(
        localStorage.getItem("userData")
      );

      const db = user?.database;

      if (!db) {
        setRowData([]);
        return;
      }

      const res = await axiosConfig.get(
        `/receipt/view-reciept/${db}`
      );

      console.log("Receipt API Response:", res);

      const receipts =
        res?.data?.Receipts ||
        res?.data?.receipts ||
        res?.data?.data ||
        [];

      // Only PAYMENT type
      const payments = receipts.filter(
        (ele) =>
          String(ele?.type || "")
            .trim()
            .toLowerCase() === "payment"
      );

      // Latest first
      const sorted = [...payments].sort(
        (a, b) =>
          new Date(b.date) - new Date(a.date)
      );

      setRowData(sorted);
    } catch (err) {
      console.error(
        "Payment API Error:",
        err?.response?.data || err
      );

      swal("Error", "Error loading payments!", "error");

      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  // Delete Payment
  const handleDelete = async (id) => {
    const confirm = await swal({
      title: "Are you sure?",
      text: "You want to delete this payment?",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });

    if (!confirm) return;

    try {
      setLoading(true);

      await axiosConfig.delete(
        `/receipt/delete-payment/${id}`
      );

      await swal(
        "Deleted!",
        "Payment deleted successfully!",
        "success"
      );

      await fetchPayments();
    } catch (err) {
      console.error(
        "DELETE PAYMENT ERROR =>",
        err?.response?.data || err
      );

      await swal(
        "Error",
        "Unable to delete payment!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Get Particular
  const getParticular = (item) => {
    if (item?.expenseId) {
      return item?.expenseId?.title || "-";
    }

    if (item?.partyId) {
      return item?.partyId?.CompanyName || "-";
    }

    if (item?.userId) {
      return item?.userId?.firstName || "-";
    }

    if (item?.transporterId?.companyName) {
      return item?.transporterId?.companyName || "-";
    }

    if (item?.bankSelect) {
      return item?.bankSelect || "-";
    }

    if (item?.bankDetails?.length > 0) {
      return item?.bankDetails[0]?.bankName || "-";
    }

    return "-";
  };

  return (
    <div className="erp-page-grid">

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <h2 style={{ margin: 0 }}>
          Payment
        </h2>

        <motion.button
          type="button"
          className="erp-btn erp-btn-primary"
          onClick={() =>
            navigate(
              "/transactions?view=create-payment"
            )
          }
          whileTap={{ scale: 0.97 }}
        >
          <PlusCircle size={15} />
          Create Payment
        </motion.button>
      </div>

      {/* Table Card */}
      <motion.div
        className="erp-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div className="erp-card-head">
          <div>
            <h3>Payments</h3>
            <p>Manage all payment transactions</p>
          </div>
        </div>

        <div
          className="erp-table-wrap"
          style={{ overflowX: "auto" }}
        >
          <table className="erp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Particular</th>
                <th>Amount</th>
                <th>Payment Mode</th>
                <th>Remark</th>
                <th
                  style={{
                    textAlign: "center",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "25px",
                    }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : rowData.length > 0 ? (
                rowData.map((item, index) => (
                  <motion.tr
                    key={item?._id || index}
                    initial={{
                      opacity: 0,
                      y: 5,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                  >
                    <td>{index + 1}</td>

                    <td>
                      {item?.date
                        ? item.date.split("T")[0]
                        : "NA"}
                    </td>

                    <td>
                      {getParticular(item)}
                    </td>

                    <td>
                      ₹{item?.amount || 0}
                    </td>

                    <td>
                      {item?.paymentMode || "NA"}
                    </td>

                    <td>
                      {item?.remark || ""}
                    </td>

                    {/* Action */}
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {/* Edit */}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Edit Payment"
                          onClick={() =>
                            navigate(
                              `/transactions?view=edit-payment-${item?._id}`
                            )
                          }
                        >
                          <Edit size={15} />
                        </button>

                        {/* Delete */}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete Payment"
                          onClick={() =>
                            handleDelete(item?._id)
                          }
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "25px",
                    }}
                  >
                    No payments available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}